import type { User } from '@/stores/auth.js';
import { computed } from 'vue';
import { defineStore } from 'pinia';
import type { PokerlyWs } from '@/services/pokerly-ws/index.js';
import { useSessionStateStore, RoundStates } from '@/stores/session-state.ts';
import { useParticipantsStore } from '@/stores/participants.ts';
import { useVotingStore } from '@/stores/voting.ts';
import { useSessionInteractionsStore } from '@/stores/session-interactions.ts';
import { useSessionInitializer } from '@/composables/use-session-initializer.ts';
import type { Participant } from '@/types/participant.js';
import type { VotingScaleSize } from '@pokerly/contracts';

// Re-export for backward compatibility
export { RoundStates, type RoundState } from '@/stores/session-state.ts';

/**
 * Session store facade - provides backward-compatible API while delegating to focused stores.
 *
 * This store coordinates the split stores and handles WebSocket event registration.
 * New code should prefer using the focused stores directly or the useSessionInitializer composable.
 */
export const useSessionStore = defineStore('session', () => {
    const sessionState = useSessionStateStore();
    const participantsStore = useParticipantsStore();
    const votingStore = useVotingStore();
    const interactionsStore = useSessionInteractionsStore();
    const sessionInitializer = useSessionInitializer();

    // --- Initialization (delegates to initializer) ---

    function init(ws: PokerlyWs) {
        sessionInitializer.initSession(ws);
    }

    // --- Snapshot Update (delegates to initializer) ---

    function updateFromSnapshot(snapshot: Parameters<typeof sessionInitializer.updateFromSnapshot>[0]) {
        sessionInitializer.updateFromSnapshot(snapshot);
    }

    // --- Delegated Getters (backward compatibility) ---

    const estimation = computed(() => votingStore.estimation);
    const estimationOptions = computed(() => sessionState.estimationOptions);
    const roundState = computed(() => sessionState.roundState);
    const sessionStatus = computed(() => sessionState.sessionStatus);
    const sessionId = computed(() => sessionState.sessionId);
    const roundId = computed(() => sessionState.roundId);
    const roundReplayId = computed(() => sessionState.roundReplayId);
    const room = computed(() => sessionState.room);
    const isRoundClosed = computed(() => sessionState.isRoundClosed);
    const participants = computed(() => participantsStore.participants);
    const voters = computed(() => participantsStore.voters);
    const observers = computed(() => participantsStore.observers);
    const statistics = computed(() => votingStore.statistics);
    const votingScaleId = computed(() => sessionState.votingScaleId);
    const votingScaleSize = computed(() => sessionState.votingScaleSize);
    const roundVotingScale = computed(() => sessionState.roundVotingScale);
    const isSessionCreated = computed(() => sessionState.isSessionCreated);
    const isSessionActive = computed(() => sessionState.isSessionActive);
    const canVote = computed(() => sessionState.canVote);

    // --- Delegated Actions (backward compatibility) ---

    function setRoom(value: string) {
        sessionState.setRoom(value);
    }

    async function assignVotingScale(newVotingScaleId: string, newVotingScaleSize: VotingScaleSize) {
        await sessionState.assignVotingScale(newVotingScaleId, newVotingScaleSize);
    }

    function setEstimation(value: number | string) {
        if (!sessionState.canVote) {
            return;
        }
        votingStore.setEstimation(value);
    }

    function setUserType(role: 'voter' | 'observer') {
        participantsStore.setUserType(role);
    }

    function isAuthenticatedUser(participant: Participant | User): boolean {
        return participantsStore.isAuthenticatedUser(participant);
    }

    async function closeRound() {
        sessionState.setRoundState(RoundStates.CLOSED);
        await sessionState.getWs().closeRound();
    }

    async function finishRound() {
        const { roundReplayId: newRoundReplayId, roundId: newRoundId } = await sessionState.getWs().finishRound();
        sessionState.setRoundIds(newRoundId, newRoundReplayId);
        sessionState.setRoundState(RoundStates.ACTIVE);
        votingStore.clearEstimation();
        participantsStore.clearVotes();
    }

    async function replayRound() {
        const { roundReplayId: newRoundReplayId } = await sessionState.getWs().replayRound();
        sessionState.setRoundIds(sessionState.roundId!, newRoundReplayId);
        sessionState.setRoundState(RoundStates.ACTIVE);
        votingStore.clearEstimation();
        participantsStore.clearVotes();
    }

    function startRound() {
        sessionState.setRoundState(RoundStates.ACTIVE);
        votingStore.clearEstimation();
        participantsStore.clearVotes();
        sessionState.getWs().startRound();
    }

    function sendEmojiThrowInteraction(toUserId: string, emoji: string) {
        interactionsStore.sendEmojiThrowInteraction(toUserId, emoji);
    }

    function onInteractionReceived(cb: Parameters<typeof interactionsStore.onInteractionReceived>[0]) {
        interactionsStore.onInteractionReceived(cb);
    }

    return {
        // State (computed getters for backward compatibility)
        estimation,
        estimationOptions,
        roundState,
        sessionStatus,
        votingScaleId,
        votingScaleSize,
        roundVotingScale,
        sessionId,
        roundId,
        roundReplayId,
        room,
        isRoundClosed,
        isSessionCreated,
        isSessionActive,
        canVote,
        participants,
        voters,
        observers,
        statistics,

        // Initialization
        init,
        updateFromSnapshot,

        // Actions
        setRoom,
        assignVotingScale,
        setEstimation,
        setUserType,
        isAuthenticatedUser,
        closeRound,
        finishRound,
        replayRound,
        startRound,
        sendEmojiThrowInteraction,
        onInteractionReceived,
    };
});
